import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerRegister = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register/customer`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'Customer'  // This will be forced to Customer by the backend
      });

      // Store token and user
      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      toast.success('Welcome to PSM Welding!');
      onLogin(response.data.user);
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-black tracking-tight uppercase text-foreground mb-2">
            PSM WELDING
          </h1>
          <p className="text-sm font-mono uppercase tracking-widest text-foreground font-semibold">
            Customer Portal Registration
          </p>
        </div>

        <div className="bg-card border-2 border-border rounded-sm p-8 shadow-xl">
          <div className="flex items-center gap-2 mb-6">
            <UserPlus size={24} className="text-primary" />
            <h2 className="text-2xl font-bold uppercase tracking-tight text-foreground">Create Account</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="uppercase text-xs tracking-wide font-bold text-foreground">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="uppercase text-xs tracking-wide font-bold text-foreground">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                className="mt-1 font-mono"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="uppercase text-xs tracking-wide font-bold text-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1"
                required
                minLength={6}
              />
              <p className="text-xs text-foreground/70 mt-1 font-medium">At least 6 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="uppercase text-xs tracking-wide font-bold text-foreground">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                className="mt-1"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider mt-6"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Register'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="font-mono uppercase text-xs text-foreground font-bold hover:bg-muted"
            >
              <ArrowLeft size={14} className="mr-2" />
              Back to Login
            </Button>
          </div>

          <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-sm">
            <p className="text-xs text-foreground/80 text-center font-medium">
              ℹ️ Customer accounts can view quotes, invoices, projects, and submit estimate requests.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegister;
